<template>
  <div class="polarization-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$I = I₀ cos²θ (马吕斯定律)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>检偏器旋转角 (θ)</label>
            <span class="val">{{ angle }}°</span>
          </div>
          <div class="hud-item orange">
            <label>透射光强比例 (I/I₀)</label>
            <span class="val">{{ (intensity * 100).toFixed(0) }} <small>%</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240">
          <defs>
             <filter id="glow-wave"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <linearGradient id="beam-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#fff" stop-opacity="0.3" />
                <stop offset="100%" stop-color="#38bdf8" stop-opacity="0.8" />
             </linearGradient>
          </defs>

          <!-- 1. 自然光区 (Unpolarized) -->
          <g v-for="x in [40, 70, 100]" :key="'nat'+x" :transform="`translate(${x}, 120)`">
             <line x1="0" y1="-25" x2="0" y2="25" stroke="#94a3b8" stroke-width="1.5" opacity="0.4" />
             <line x1="-25" y1="0" x2="25" y2="0" stroke="#94a3b8" stroke-width="1.5" opacity="0.4" />
             <path d="M -15,-15 L 15,15 M -15,15 L 15,-15" stroke="#94a3b8" stroke-width="1.5" opacity="0.2" />
          </g>
          <text x="70" y="165" text-anchor="middle" fill="#64748b" font-size="9">自然光 (多向振动)</text>

          <!-- 2. 起偏器 A (Polarizer - Fixed Vertical) -->
          <g transform="translate(140, 50)">
             <rect width="12" height="140" fill="#1e293b" stroke="#475569" stroke-width="2" rx="4" />
             <line v-for="i in 10" :key="'p1'+i" x1="2" :y1="i*13" x2="10" :y2="i*13" stroke="#64748b" />
             <text x="6" y="155" text-anchor="middle" fill="#94a3b8" font-size="10" font-weight="bold">P₁ (固定)</text>
          </g>

          <!-- 3. 线偏振光波形 -->
          <path :d="polarizedWavePath" fill="none" stroke="#38bdf8" stroke-width="3" filter="url(#glow-wave)" opacity="0.8" />

          <!-- 4. 检偏器 B (Analyzer - Rotatable) -->
          <g :transform="`translate(280, 50) rotate(${angle}, 6, 70)`">
             <rect width="12" height="140" fill="#1e293b" :stroke="intensity > 0.1 ? '#38bdf8' : '#e11d48'" stroke-width="2" rx="4" />
             <!-- 栅格 -->
             <line v-for="i in 10" :key="'p2'+i" x1="2" :y1="i*13" x2="10" :y2="i*13" :stroke="intensity > 0.1 ? '#38bdf8' : '#e11d48'" />
             <text x="6" y="155" text-anchor="middle" fill="#38bdf8" font-size="10" font-weight="bold" :transform="`rotate(${-angle}, 6, 155)`">P₂ (θ)</text>
          </g>

          <!-- 5. 透射光亮斑 -->
          <rect x="310" y="60" width="80" height="120" :fill="`rgba(56, 189, 248, ${intensity * 0.4})`" filter="url(#glow-wave)" />
          <text x="350" y="185" text-anchor="middle" :fill="intensity > 0.1 ? '#38bdf8' : '#64748b'" font-size="10" font-weight="bold">
             {{ intensity < 0.05 ? '消光' : '透射' }}
          </text>
        </svg>

        <div class="angle-dial-container">
           <svg width="60" height="60" viewBox="0 0 60 60">
              <circle cx="30" cy="30" r="28" fill="none" stroke="#1e293b" stroke-width="2" />
              <line x1="30" y1="30" :x2="30 + Math.sin(angleRad)*25" :y2="30 - Math.cos(angleRad)*25" stroke="#38bdf8" stroke-width="3" stroke-linecap="round" />
           </svg>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-col gap-2">
              <div class="flex items-center justify-between">
                 <span class="label">旋转检偏器角度 θ (0° ~ 180°)</span>
                 <span class="val-tag">{{ angle }}°</span>
              </div>
              <el-slider v-model="angle" :min="0" :max="180" :step="1" size="small"></el-slider>
              <div class="flex justify-between mt-1">
                 <el-button type="info" plain size="mini" @click="angle = 0">平行 (全透)</el-button>
                 <el-button type="info" plain size="mini" @click="angle = 45">45° (半透)</el-button>
                 <el-button type="danger" plain size="mini" @click="angle = 90">正交 (消光)</el-button>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>物理规律：</strong>只有振动方向与起偏器狭缝平行的波分量才能通过。<strong>马吕斯定律</strong>公式 $I = I_0 \cos^2\theta$ 描述了透射光强与两偏振片夹角的关系。由于纵波（如声波）不具有偏振特性，这一实验完美证明了<strong>光是横波</strong>。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PolarizationViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      angle: 0,
      t: 0,
      timer: null
    }
  },
  computed: {
    angleRad() { return this.angle * Math.PI / 180; },
    intensity() {
       return Math.cos(this.angleRad) * Math.cos(this.angleRad);
    },
    polarizedWavePath() {
       let points = [];
       const amp = 40;
       for (let x = 152; x <= 280; x += 4) {
          // Vertical polarization after P1
          const y = 120 - amp * Math.sin((x - 152) * 0.1 - this.t);
          points.push(`${x},${y.toFixed(1)}`);
       }
       return "M " + points.join(" L ");
    }
  },
  mounted() {
     this.timer = setInterval(() => { this.t += 0.15; }, 30);
  },
  beforeDestroy() { clearInterval(this.timer); }
}
</script>

<style scoped lang="scss">
.polarization-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #f0f9ff; color: #0369a1; border: 1px solid #bae6fd; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; }
      &.cyan { border-left: 3px solid #06b6d4; }
      &.orange { border-left: 3px solid #38bdf8; }
    }
  }
}

.angle-dial-container { position: absolute; bottom: 12px; right: 12px; background: rgba(15,23,42,0.8); border-radius: 50%; padding: 4px; opacity: 0.8; }

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-col { flex-direction: column; } .gap-2 { gap: 8px; } .justify-between { justify-content: space-between; } .items-center { align-items: center; }
.val-tag { background: #f1f5f9; color: #475569; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; }

.discovery-card-compact {
  background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px;
  i { color: #0ea5e9; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #075985; } }
}
</style>
